import React from "react";
import { ShieldCheck } from "lucide-react";

/**
 * PriceVerifiedPill — small social-proof badge displayed under a verified price.
 *
 * Renders only when the campsite has a crowdsourced price that an admin has
 * applied (i.e. `price_verified_at` is set). The verified_count comes from
 * how many open camper reports were merged at apply-time.
 *
 *   ✓ Verified by 3 campers · 5 days ago
 *
 * Designed to be drop-in: just pass the campsite document.
 */
export default function PriceVerifiedPill({ site, className = "" }) {
  if (!site) return null;
  const verifiedAt = site.price_verified_at;
  const count = site.price_verified_count;
  if (!verifiedAt || !count || count < 1) return null;

  // Days ago — uses calendar days so the copy stays human ("Today", "Yesterday")
  let daysAgo = 0;
  try {
    const d = new Date(verifiedAt);
    daysAgo = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  } catch {
    return null;
  }

  let when;
  if (daysAgo === 0) when = "today";
  else if (daysAgo === 1) when = "yesterday";
  else if (daysAgo < 30) when = `${daysAgo} days ago`;
  else if (daysAgo < 365) when = `${Math.round(daysAgo / 30)} months ago`;
  else when = `${Math.round(daysAgo / 365)} years ago`;

  const camperWord = count === 1 ? "camper" : "campers";

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#fff4ee] border border-[#ff4500] text-[#ff4500] mono text-[10px] sm:text-[11px] uppercase tracking-wider font-bold ${className}`}
      data-testid="price-verified-pill"
      title={`Crowdsourced price verified by ${count} ${camperWord} on ${new Date(verifiedAt).toLocaleDateString("en-GB")}`}
    >
      <ShieldCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={2.2} />
      <span>Verified by {count} {camperWord} · {when}</span>
    </div>
  );
}
